import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import classNames from 'classnames';

import i18n from '@nextroll/ar-i18n';
import SlidingModal from '@nextroll/ar-sliding-modal';

import { useRoflStore } from 'store/roflStore';
import { INodeIssue, IWorkflowActionConfig, IWorkflowSchemaTrigger, WORKFLOW_BASE } from 'types/rofl';

import { NodePanelBase } from './NodePanelBase';
import { NodeInnerCard, INodeInnerCardProps } from '../NodeInnerCard/NodeInnerCard';


export interface INodeBasePanelElementConfig {
    key?: string;
    component: (...args: any[]) => JSX.Element | null;
    isRequired?: boolean;
    isHidden?: boolean;
    validate?: (...args: any[]) => boolean;
    getTextForNode?: (config: IWorkflowActionConfig, abortSignal: AbortSignal) => Promise<string>;
}

export interface INodeBaseProps<T> {
    // content of the node; it is mandatory for canvas nodes
    children?: React.ReactNode | React.ReactNode[]; // possible to have multiple inner cards

    id?: string;
    title?: string;
    icon?: JSX.Element;
    className?: string;

    /**
     * panel specific properties
     *
     * note: if `NodeInnerCard` components are used as the node content, then use
     * the corresponding inner card panel properties (with the same names) instead
     */
    panelSubtitle?: string;
    panelElementConfigs?: INodeBasePanelElementConfig[];
    panelClassName?: string;

    /**
     * We don't want the panel content updates the node content until the "Apply" button is pressed,
     * meaning it is necessary to keep 2 copies of the content data: 1 for the panel and 1 for the node.
     *
     * `content.current` is the panel content that is currently modified by the user. It will
     * 1) replace by the original node content `content.original` when hitting the "Apply" button, or
     * 2) be discarded when hitting the "Cancel" button.
     */
    content?: { original?: T; current?: T };
    setContent?: React.Dispatch<React.SetStateAction<{ original?: T; current?: T } | undefined>>;

    loading?: boolean; // look-and-feel when node is loading content
    preventApply?: boolean; // whether or not to allow the apply button to be clicked
    active?: boolean; // look-and-feel when node is selected
    forceSlidingModalShow?: boolean; // whether to force the sliding modal to appear or disappear, undefined allows the default behavior
    error?: boolean; // look-and-feel when node has errors
    warning?: boolean; // look-and-feel when node has warnings
    // TODO: Sort this out..
    // eslint-disable-next-line react/no-unused-prop-types
    validationErrors?: INodeIssue[];
    // eslint-disable-next-line react/no-unused-prop-types
    validationWarnings?: INodeIssue[];
    // eslint-disable-next-line react/no-unused-prop-types
    loadingWorkflow?: boolean;
    // Needed to do this because nodeBase is the same for nodeContainer and ActionNodes + triggerNodes
    // passing validation errors inside Data Context should resolve this.
    disableArrow?: boolean;

    // extra buttons inserted between the cancel and the apply buttons in the details panel
    actionButtons?: JSX.Element[];

    onSelect?: () => void;
    onClose?: () => void | boolean;
    onApply?: (data?: Record<string, any>) => void | boolean;
}

export const NodeBase = <T, >(props: INodeBaseProps<T>) => {
    const [
        updateContentData,
        showRequiredError,
        hideRequiredError,
    ] = useRoflStore(useShallow(state => [
        state.updateContentData,
        state.showRequiredError,
        state.hideRequiredError,
    ]));

    /**
     * get query parameters from the URL
     */
    const { search } = useLocation();
    const templateEid = useMemo(() => new URLSearchParams(search).get('template') || undefined, [search]);

    /**
     * classnames of the node
     */
    const nodeClasses = useMemo(() => classNames('workflow-node', {
        active: !!props.active, // when the node is selected
        error: !!props.error, // when the node is in error state
        warning: !!props.warning && !props.error, // when the node is in warning state,
        [`${props.className}`]: !!props.className, // external node classname
    }), [props.active, props.error, props.warning, props.className]);

    /**
     * classnames of the side panel attached to the node
     */
    const panelClasses = useMemo(() => classNames('workflow-canvas-side-panel-content', {
        [`workflows-node-panel-template-${templateEid}`]: !!templateEid, // when creating with a template
    }), [templateEid]);

    const cannotApplyFromError = useMemo(
        () => (props.panelElementConfigs || [])
            .some(elementConfig => elementConfig.validate?.() === false),
        [props.panelElementConfigs],
    );

    const handleSelect = () => {
        !props.loading && props.onSelect?.();
        // show errors when re-opening panel when node has errors
        if (props.error && cannotApplyFromError) {
            showRequiredError();
        }
    };

    const handleApply = () => {
        if (props.preventApply) {
            return;
        }

        if (cannotApplyFromError) {
            showRequiredError();
            return;
        }

        const shouldUpdateData = props.onApply?.();
        if (shouldUpdateData !== false) {
            updateContentData(props.id, props.content?.current); // update the data context hook
            props.setContent?.({ ...props.content, original: props.content?.current }); // replace original data with the current copy
            hideRequiredError();
        }
    };

    const handleClose = () => {
        const shouldUpdateData = props.onClose?.();
        hideRequiredError();
        if (shouldUpdateData !== false) {
            props.setContent?.({ ...props.content, current: props.content?.original }); // discard the current data
        }
    };
    return (
        <>
            <div data-testid='workflow-node-content' className={nodeClasses} onClick={handleSelect}>
                <div className='workflow-node-card'>

                    {/* here is the node card (title + content) */}
                    <div className='workflow-node-card-header'>
                        {props.icon}
                        <strong>{props.title}</strong>
                        {/* here is the error tag at the header right corner */}
                        {(props.error && (
                            <div className='workflow-node-card-error ar-label-error'>
                                <i className='ar-icon ar-icon-fw fa-exclamation-circle' />
                            &nbsp;Error
                            </div>
                        ))
                        || (props.warning && (
                            <div className='workflow-node-card-warning ar-label-warning'>
                                <i className='ar-icon ar-icon-fw fa-exclamation-triangle text-warning' />
                               &nbsp;Warning
                            </div>
                        ))}
                    </div>
                    <div className='workflow-node-card-body'>
                        {props.children}
                    </div>

                    {props.loading && (
                        <>
                            <div
                                data-testid='workflow-node-card-loading-overlay'
                                className='workflow-node-card-loading-overlay'
                            />
                            <span className='loading-spinner' />
                        </>
                    )}
                </div>
                {!props.disableArrow && <div className='workflow-node-card-arrow' />}
            </div>

            <SlidingModal
                show={props.forceSlidingModalShow !== undefined ? props.forceSlidingModalShow : !!props.active }
                title={props.title || ''}
                size={SlidingModal.SLIDING_MODAL_MEDIUM}
                onClose={handleClose}
                className='workflow-canvas-side-panel'
            >
                <div data-testid='workflow-node-panel-content' className={panelClasses}>
                    <NodePanelBase
                        subtitle={props.panelSubtitle}
                        elements={props.panelElementConfigs}
                        className={props.panelClassName}
                    />
                </div>
                <div className='workflow-canvas-side-panel-actions'>
                    <button data-testid='workflow-node-panel-btn-apply' role='button' type='button' className='btn btn-primary' onClick={handleApply}>
                        {props.preventApply && <span className='loading-spinner-small' data-testid='workflow-node-panel-btn-apply-spinner' />}
                        {i18n.gettext('Apply')}
                    </button>
                    {props.actionButtons}
                    <button data-testid='workflow-node-panel-btn-cancel' role='button' type='button' className='btn btn-default' onClick={handleClose}>
                        {i18n.gettext('Cancel')}
                    </button>
                </div>
            </SlidingModal>
        </>
    );
};

export const TriggerNodeBase = <T, >(props: INodeBaseProps<T>) => {
    const [content, base] = useRoflStore(state => [state.contentData[props.id || ''], state.base]);
    const overwriteNodeContent = useMemo(() => {
        const data = content?.data as IWorkflowSchemaTrigger;

        // account-based vs. contact-based workflows are handled differently for now
        if (base === WORKFLOW_BASE.ACCOUNTS) {
            return data?.config?.list_rule && props.children;
        }
        return (data?.contact_list_eid || data?.rules) && props.children;
    }, [content, base, props.children]);
    return (
        <NodeBase
            title={props.title || i18n.gettext('Trigger')}
            icon={props.icon || <i className='ar-icon ar-icon-fw fa-sliders-h' />}
            {...props}
        >
            {overwriteNodeContent || (
                <>
                    <p>{i18n.gettext('How do you want to trigger this workflow?')}</p>
                    <button role='button' className='btn btn-primary' style={{ width: '100%' }} type='button'>
                        {i18n.gettext('Select Trigger')}
                    </button>
                </>
            )}
        </NodeBase>
    );
};

/**
 * this is the universal component of action nodes, which should contain 1 of the following 2 DOM structures:
 *
 * 1. content with multiple `NodeInnerCard` instances, each one contains the corresponding inner card and panel configs:
 *
 *     <ActionNodeBase<T>>
 *         <NodeInnerCard ... />
 *         <NodeInnerCard ... />
 *         <NodeInnerCard ... />
 *     </ActionNodeBase>
 *
 * in which only `NodeInnerCard` children are recognized and rendered, or;
 *
 * 2. backward compatible with old action node structure (contact-based action node) that only contains a single DOM
 *
 *     <ActionNodeBase<T>>
 *        <>...</>
 *     </ActionNodeBase>
 */
export const ActionNodeBase = <T, >(props: INodeBaseProps<T> & { initPage?: number }) => {
    const [content, shouldShowRequiredError] = useRoflStore(state => [
        state.contentData[props.id || ''],
        state.shouldShowRequiredError,
    ]);

    /**
     * panel page index associated with the selected `NodeInnerCard` instance
     */
    const [page, setPage] = useState(props.initPage || 0);

    /**
     * here we only deal with `NodeInnerCard` instances
     */
    const innerCards = useMemo(
        () => React.Children.toArray(props.children)
            .filter(child => React.isValidElement(child) && (child as React.ReactElement).type === NodeInnerCard),
        [props.children],
    ) as React.ReactElement<INodeInnerCardProps>[];

    /**
     * pull the elelment configs of each page from card instances and flatten them into an list
     */
    const panelElementConfigs = useMemo(() => {
        if (innerCards.length <= 0) {
            return undefined; // no inner cards, use the parent configs directly
        }
        return innerCards
            .map((child, i) => (child.props.panelElementConfigs || []).map((config: INodeBasePanelElementConfig) => ({
                ...config,

                // here we are appending necessary attributes based iterating through each inner card
                page: i,
                isHidden: page !== i || !!config.isHidden,
                validate: (ignorePage?: boolean) => (!ignorePage && page < i) || config.validate?.(),
            })))
            .flat();
    }, [innerCards, page]) as (INodeBasePanelElementConfig & { page: number })[];

    /**
     * a referrence to the current selected card for easy access
     */
    const currCardProps = useMemo(() => innerCards[page]?.props, [innerCards, page]);

    /**
     * jump to the page that contains the first invalid component when the action panel is opened with errors
     */
    useEffect(() => {
        if (!shouldShowRequiredError || !currCardProps) {
            return; // do NOT show errors outside of the validation stage
        }
        const firstPageHasError = panelElementConfigs?.find(config => config.validate?.(true) === false)?.page;
        firstPageHasError !== undefined && setPage(firstPageHasError);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldShowRequiredError, setPage]); // we don't want it to be triggered by `panelElementConfigs` changes

    /**
     * event handlers
     */
    const handleApply = useCallback(() => {
        setPage(p => (p + 1 >= innerCards.length ? (props.initPage || 0) : p + 1)); // progress to the next page
        return currCardProps?.onApply?.() || false;
    }, [innerCards.length, props.initPage, currCardProps, setPage]);

    const handleClose = useCallback(() => {
        setPage((props.initPage || 0));
        props.onClose?.();
        return false;
    }, [props.initPage, props.onClose, setPage]); // eslint-disable-line react-hooks/exhaustive-deps

    /**
     * this is the card content body
     */
    const body = useMemo(() => {
        if (!content?.data && !props.content?.current) {
            return (
                <>
                    <p>{i18n.gettext('What action should take place?')}</p>
                    <button role='button' className='btn btn-default' type='button' style={{ width: '100%' }}>
                        {i18n.gettext('Choose Action')}
                    </button>
                </>
            );
        }
        if (innerCards.length <= 0) {
            return props.children; // no inner card instances; this is probably the old node content
        }

        return innerCards.map((child, i) => React.cloneElement(child, {
            active: props.active && page === i,
            onSelect: () => setPage(i),
            onRemove: child.props.onRemove && (() => {
                child.props.onRemove?.();
                setPage(0);
            }),
        }));
    }, [props.children, props.content, props.active, innerCards, content?.data, page, setPage]);

    /**
     * toggle the visibility of the action buttons based on the page number to avoid re-rendering repeatedly
     */
    const actionButtons = useMemo(() => {
        if (innerCards.length <= 0) {
            return []; // no inner card instances; this is probably the old node content
        }
        return innerCards
            .map((card, i) => (card.props.actionButtons || []).map(button => React.cloneElement(button, {
                show: page === i, // use the `show` prop to control the visibility of any action button
            })))
            .flat();
    }, [innerCards, page]);

    return (
        <NodeBase
            {...props}
            {...currCardProps ? {
                panelSubtitle: currCardProps?.panelSubtitle,
                panelElementConfigs,
                actionButtons,
                onApply: handleApply,
            } : {}} // overwrite panel configs if it contains inner cards
            title={props.title || i18n.gettext('Action')}
            icon={props.icon || <i className='ar-icon ar-icon-fw fa-bolt' />}
            className='workflow-action'
            onClose={handleClose}
        >
            {body}
        </NodeBase>
    );
};

export const FinishNode = () => (
    <div className='workflow-text-node'>
        <i className='ar-icon ar-icon-fw fa-flag-checkered' />
        <strong>{i18n.gettext('Finish')}</strong>
    </div>
);
